/**
 * GuitarFinds — Featured Carousel
 * Uses Shopify/Dawn native card-product markup rendered by Liquid.
 * JS only owns carousel behavior: shuffle, arrows, dots, drag, resize state.
 */
(function () {
  'use strict';

  class FeaturedCarousel {
    constructor(section) {
      this.section = section;
      this.el = section.matches('[data-featured-carousel]')
        ? section
        : section.querySelector('[data-featured-carousel]');

      if (!this.el) return;

      this.track = this.el.querySelector('[data-carousel-track]');
      this.slides = Array.from(this.el.querySelectorAll('[data-carousel-slide]'));
      this.prevBtn = this.el.querySelector('[data-carousel-prev]');
      this.nextBtn = this.el.querySelector('[data-carousel-next]');
      this.dotsContainer = this.el.querySelector('[data-carousel-dots]');
      this.outer = this.el.querySelector('[data-carousel-outer]');

      if (!this.track || !this.slides.length) return;

      this.settings = this._readSettings();
      this.currentIndex = 0;
      this.pageCount = 1;
      this.dots = [];
      this.rafId = null;
      this.dragRafId = null;
      this.abortController = new AbortController();
      this._ro = null;
      this._io = null;
      this._onMouseMove = null;
      this._onMouseUp = null;

      if (this.settings.randomize) this._shuffle();
      this._injectSpacers();

      if (this.outer) this.outer.setAttribute('data-loading', '');

      requestAnimationFrame(() => {
        this.track.scrollLeft = 0;
        this.currentIndex = 0;
        this._buildDots();
        this._bindArrows();
        this._bindDrag();
        this._bindScroll();
        this._updateState();

        if (this.outer) this.outer.removeAttribute('data-loading');

        this._ro = new ResizeObserver(() => {
          this._rebuildDots();
          this._updateState();
        });
        this._ro.observe(this.track);
      });
    }

    _readSettings() {
      return {
        randomize: this.el.dataset.randomize === 'true',
        showDots: this.el.dataset.showDots !== 'false'
      };
    }

    _injectSpacers() {
      this.track.querySelectorAll('.featured-carousel__spacer').forEach((spacer) => spacer.remove());

      const spacer = document.createElement('li');
      spacer.className = 'featured-carousel__spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.cssText = [
        'flex: 0 0 var(--carousel-edge-pad, 6rem)',
        'list-style: none',
        'pointer-events: none',
        'scroll-snap-align: start'
      ].join(';');

      this.track.prepend(spacer);
      this.track.append(spacer.cloneNode(false));
    }

    _shuffle() {
      for (let i = this.slides.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const nodeA = this.slides[i];
        const nodeB = this.slides[j];
        const refA = nodeA.nextSibling === nodeB ? nodeA : nodeA.nextSibling;

        this.track.insertBefore(nodeA, nodeB);
        this.track.insertBefore(nodeB, refA);
        [this.slides[i], this.slides[j]] = [this.slides[j], this.slides[i]];
      }
    }

    _slidesPerPage() {
      if (!this.slides.length) return 1;
      return Math.max(1, Math.round(this.track.clientWidth / this.slides[0].offsetWidth));
    }

    _calcPageCount() {
      return Math.max(1, Math.ceil(this.slides.length / this._slidesPerPage()));
    }

    _slideIndexToPage(index) {
      return Math.floor(index / this._slidesPerPage());
    }

    _pageToSlideIndex(page) {
      return Math.min(page * this._slidesPerPage(), this.slides.length - 1);
    }

    _buildDots() {
      if (!this.dotsContainer) return;

      if (!this.settings.showDots) {
        this.dotsContainer.hidden = true;
        return;
      }

      this.pageCount = this._calcPageCount();
      this.dotsContainer.innerHTML = '';
      this.dots = [];

      if (this.pageCount <= 1) {
        this.dotsContainer.hidden = true;
        return;
      }

      this.dotsContainer.hidden = false;

      for (let page = 0; page < this.pageCount; page += 1) {
        const button = document.createElement('button');
        button.className = 'featured-carousel__dot';
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-label', `Go to carousel page ${page + 1} of ${this.pageCount}`);
        button.addEventListener(
          'click',
          () => this._scrollToIndex(this._pageToSlideIndex(page)),
          { signal: this.abortController.signal }
        );
        this.dotsContainer.appendChild(button);
        this.dots.push(button);
      }
    }

    _rebuildDots() {
      const newCount = this._calcPageCount();
      if (newCount === this.pageCount) return;
      this.pageCount = newCount;
      this._buildDots();
    }

    _bindArrows() {
      this.prevBtn?.addEventListener(
        'click',
        () => {
          const previousPage = Math.max(0, this._slideIndexToPage(this.currentIndex) - 1);
          this._scrollToIndex(this._pageToSlideIndex(previousPage));
        },
        { signal: this.abortController.signal }
      );

      this.nextBtn?.addEventListener(
        'click',
        () => {
          const nextPage = Math.min(this.pageCount - 1, this._slideIndexToPage(this.currentIndex) + 1);
          this._scrollToIndex(this._pageToSlideIndex(nextPage));
        },
        { signal: this.abortController.signal }
      );

      this.track.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this._scrollToIndex(Math.max(0, this.currentIndex - 1));
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this._scrollToIndex(Math.min(this.slides.length - 1, this.currentIndex + 1));
          }
        },
        { signal: this.abortController.signal }
      );
    }

    _bindDrag() {
      let isMouseDown = false;
      let hasDragged = false;
      let startX = 0;
      let startScrollLeft = 0;
      let targetLeft = 0;
      let currentLeft = 0;
      const lerpAmount = 0.16;

      const disableSnap = () => {
        this.track.style.scrollSnapType = 'none';
      };

      const enableSnap = () => {
        this.track.style.scrollSnapType = '';
      };

      const lerp = () => {
        const diff = targetLeft - currentLeft;

        if (Math.abs(diff) < 0.4) {
          this.track.scrollLeft = Math.round(targetLeft);
          currentLeft = targetLeft;
          this.dragRafId = null;
          return;
        }

        currentLeft += diff * lerpAmount;
        this.track.scrollLeft = currentLeft;
        this.dragRafId = requestAnimationFrame(lerp);
      };

      const startLerp = () => {
        if (this.dragRafId) cancelAnimationFrame(this.dragRafId);
        currentLeft = this.track.scrollLeft;
        this.dragRafId = requestAnimationFrame(lerp);
      };

      const snapToNearest = () => {
        const trackLeft = this.track.getBoundingClientRect().left;
        let closest = 0;
        let closestDistance = Infinity;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          if (distance < closestDistance) {
            closest = index;
            closestDistance = distance;
          }
        });

        this._scrollToIndex(closest);
      };

      const onMouseDown = (event) => {
        if (event.button !== 0) return;

        isMouseDown = true;
        hasDragged = false;
        startX = event.clientX;
        startScrollLeft = this.track.scrollLeft;
        targetLeft = this.track.scrollLeft;
        currentLeft = this.track.scrollLeft;

        if (this.dragRafId) {
          cancelAnimationFrame(this.dragRafId);
          this.dragRafId = null;
        }

        event.preventDefault();
      };

      const onMouseMove = (event) => {
        if (!isMouseDown) return;

        const delta = event.clientX - startX;

        if (!hasDragged && Math.abs(delta) > 4) {
          hasDragged = true;
          disableSnap();
          this.track.classList.add('is-dragging');
          this.track.style.cursor = 'grabbing';
        }

        if (!hasDragged) return;

        const maxLeft = this.track.scrollWidth - this.track.clientWidth;
        targetLeft = Math.max(0, Math.min(startScrollLeft - delta, maxLeft));

        if (!this.dragRafId) startLerp();
      };

      const onMouseUp = () => {
        if (!isMouseDown) return;

        isMouseDown = false;
        this.track.classList.remove('is-dragging');
        this.track.style.cursor = '';

        if (!hasDragged) return;
        hasDragged = false;

        const afterLerp = () => {
          if (this.dragRafId) {
            requestAnimationFrame(afterLerp);
            return;
          }
          enableSnap();
          snapToNearest();
        };

        requestAnimationFrame(afterLerp);
      };

      const onTouchStart = () => {
        if (!this.dragRafId) return;
        cancelAnimationFrame(this.dragRafId);
        this.dragRafId = null;
      };

      this._onMouseMove = onMouseMove;
      this._onMouseUp = onMouseUp;

      this.track.addEventListener('mousedown', onMouseDown, { signal: this.abortController.signal });
      this.track.addEventListener('touchstart', onTouchStart, { passive: true, signal: this.abortController.signal });
      window.addEventListener('mousemove', this._onMouseMove, { signal: this.abortController.signal });
      window.addEventListener('mouseup', this._onMouseUp, { signal: this.abortController.signal });
    }

    _bindScroll() {
      this._io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;

            const index = this.slides.indexOf(entry.target);
            if (index === -1 || index === this.currentIndex) continue;

            this.currentIndex = index;
            this._updateState();
          }
        },
        { root: this.track, threshold: 0.5 }
      );

      this.slides.forEach((slide) => this._io.observe(slide));
    }

    _scrollToIndex(index) {
      const slide = this.slides[index];
      if (!slide) return;

      this.currentIndex = index;
      const spacer = this.track.querySelector('.featured-carousel__spacer');
      const spacerWidth = spacer ? spacer.offsetWidth : 0;
      const target = Math.max(0, slide.offsetLeft - spacerWidth);

      this.track.scrollTo({ left: target, behavior: 'smooth' });
      this._updateState();
    }

    _updateState() {
      const currentPage = this._slideIndexToPage(this.currentIndex);
      const lastPage = this.pageCount - 1;

      if (this.prevBtn) this.prevBtn.disabled = currentPage <= 0;
      if (this.nextBtn) this.nextBtn.disabled = currentPage >= lastPage;

      this.dots.forEach((dot, page) => {
        const active = page === currentPage;
        dot.setAttribute('aria-selected', String(active));
        dot.setAttribute('tabindex', active ? '0' : '-1');
      });

      const slidesPerPage = this._slidesPerPage();
      this.slides.forEach((slide, index) => {
        const visible = index >= this.currentIndex && index < this.currentIndex + slidesPerPage;
        slide.setAttribute('aria-hidden', String(!visible));
      });
    }

    destroy() {
      this.abortController.abort();
      if (this._onMouseMove) window.removeEventListener('mousemove', this._onMouseMove);
      if (this._onMouseUp) window.removeEventListener('mouseup', this._onMouseUp);
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      if (this.dragRafId) cancelAnimationFrame(this.dragRafId);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }
  }

  function init(root = document) {
    root.querySelectorAll('[data-featured-carousel]').forEach((section) => {
      if (!section._featuredCarousel) section._featuredCarousel = new FeaturedCarousel(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-featured-carousel]');
    if (!section?._featuredCarousel) return;
    section._featuredCarousel.destroy();
    delete section._featuredCarousel;
  });
})();
